<script setup lang="ts">
import DashboardSummaryHeader from '@/components/rc/work-order-dashboard/SummaryHeader.vue'
import { useWorkOrder } from '@/composables/rc/work-order'
import DetailsViewLayout from '@/views/layouts/DetailsViewLayout.vue'
import type { TabsPaneContext } from 'element-plus'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useWorkOrderDetails } from '@/composables/rc/work-order-details'
import { storeToRefs } from 'pinia'
import { onMounted, ref } from 'vue'
import { useOIDCStore } from '@/stores/oidc.store'

const props = defineProps<{ number: string }>()

const oidcStore = useOIDCStore()
const { isRcAppManagerUser, isCustomer: isEndCustomer } = storeToRefs(oidcStore)

const { isCustomer: isWorkOrderCustomer, isBillingRelevantForCurrentUser } = useWorkOrder(props.number)

const router = useRouter()
const activeTab = computed(() => router.currentRoute.value.name?.toString())
const previousRoute = ref()

const { data: workOrderDetails } = useWorkOrderDetails(props.number)

const handleTabClick = (tab: TabsPaneContext) => {
  router.push({ name: tab.props.name!.toString() })
}

const handleNavigateToPreviousScreen = () => {
  if (previousRoute.value) {
    router.push(previousRoute.value)
  } else {
    router.back()
  }
}

// Store the previous route when component is mounted
onMounted(() => {
  // Get the previous route from router's history
  // Adjusted to type safety with TypeScript
  previousRoute.value = router.options.history.state.back
})
</script>

<template>
  <DetailsViewLayout
    title="Repair Work Order Dashboard"
    fixed-header
    back-button-text="Back to previous screen"
    @on-back-click="handleNavigateToPreviousScreen"
  >
    <template #navigation>
      <DashboardSummaryHeader :work-order-number="number" />
      <el-divider />
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane label="Summary" name="rc-work-order-dashboard-overview" />
        <el-tab-pane label="Miners" name="rc-work-order-dashboard-miners" />
        <el-tab-pane label="Logistics" name="rc-work-order-dashboard-logistics" />
        <el-tab-pane v-if="isBillingRelevantForCurrentUser" label="Billing" name="rc-work-order-dashboard-billing" />
        <el-tab-pane
          v-if="isEndCustomer || (isWorkOrderCustomer && isRcAppManagerUser) || isRcAppManagerUser"
          label="Messages"
          name="rc-work-order-dashboard-chat"
        >
          <template #label>
            <el-badge class="item" is-dot :hidden="!workOrderDetails?.has_unread_messages"> Messages </el-badge>
          </template>
        </el-tab-pane>
      </el-tabs>
    </template>
    <div class="flex-1 flex flex-col gap-6 pt-6">
      <router-view />
    </div>
  </DetailsViewLayout>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

.el-divider {
  margin: 0;
  margin-bottom: 4px;
}

:deep() {
  @include overrides.custom-el-tabs;
}
</style>
